import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import api from '../services/api'
import TeamHelmet from '../components/TeamHelmet'
import UserAvatar from '../components/UserAvatar'
import { getLeagueTeamDisplayName } from '../constants/teamNames'
import { normalizePlayableTeamAbbreviation } from '../constants/teamAssets'
import './GamesList.css'

interface GameSummary {
  id: number
  homeTeam: string
  awayTeam: string
  homeScore: number
  awayScore: number
  playedAt: string | null
  uploadedAt: string
  player1User?: {
    id: number
    username: string
    displayName: string
    discordId: string | null
    discordAvatar: string | null
    teamAbbreviation: string
  }
  player2User?: {
    id: number
    username: string
    displayName: string
    discordId: string | null
    discordAvatar: string | null
    teamAbbreviation: string
  }
}

interface UserSummary {
  id: number
  username: string
  discordUsername: string | null
  discordGlobalName: string | null
  leagueTeamName: string | null
}

export default function GamesList() {
  const navigate = useNavigate()
  const { userId, teamName, teamA, teamB } = useParams<{
    userId?: string
    teamName?: string
    teamA?: string
    teamB?: string
  }>()
  const playerUserId = userId ? Number(userId) : null
  const selectedTeamName = teamName ? decodeURIComponent(teamName) : null
  const matchupTeamA = normalizePlayableTeamAbbreviation(teamA)
  const matchupTeamB = normalizePlayableTeamAbbreviation(teamB)
  const hasMatchupFilter = Boolean(matchupTeamA && matchupTeamB)
  const [games, setGames] = useState<GameSummary[]>([])
  const [users, setUsers] = useState<UserSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchGames = async () => {
      try {
        setLoading(true)
        const [gamesResponse, usersResponse] = await Promise.all([
          api.get<GameSummary[]>('/games'),
          playerUserId || selectedTeamName
            ? api.get<UserSummary[]>('/users')
            : Promise.resolve({ data: [] as UserSummary[] }),
        ])

        setGames(gamesResponse.data)
        setUsers(usersResponse.data)
        setError(null)
      } catch (err: any) {
        console.error('Error fetching games:', err)
        setError(err.response?.data?.error || 'Failed to load games.')
      } finally {
        setLoading(false)
      }
    }

    fetchGames()
  }, [playerUserId, selectedTeamName])

  const selectedUser = useMemo(
    () => users.find(user => user.id === playerUserId) ?? null,
    [playerUserId, users]
  )

  const selectedUserDisplayName = useMemo(() => {
    if (selectedUser) {
      return selectedUser.discordGlobalName ?? selectedUser.discordUsername ?? selectedUser.username
    }

    const matchingGame = games.find(
      game => game.player1User?.id === playerUserId || game.player2User?.id === playerUserId
    )
    const matchingPlayer =
      matchingGame?.player1User?.id === playerUserId
        ? matchingGame.player1User
        : matchingGame?.player2User?.id === playerUserId
          ? matchingGame.player2User
          : null

    return matchingPlayer?.displayName ?? null
  }, [games, playerUserId, selectedUser])

  const displayedGames = useMemo(() => {
    if (matchupTeamA && matchupTeamB) {
      return games.filter(game => {
        const homeTeam = normalizePlayableTeamAbbreviation(game.homeTeam)
        const awayTeam = normalizePlayableTeamAbbreviation(game.awayTeam)

        return (
          (homeTeam === matchupTeamA && awayTeam === matchupTeamB) ||
          (homeTeam === matchupTeamB && awayTeam === matchupTeamA)
        )
      })
    }

    if (!playerUserId) {
      if (selectedTeamName) {
        const teamUserIds = new Set(
          users
            .filter(user => user.leagueTeamName === selectedTeamName)
            .map(user => user.id)
        )

        return games.filter(
          game =>
            (game.player1User?.id != null && teamUserIds.has(game.player1User.id)) ||
            (game.player2User?.id != null && teamUserIds.has(game.player2User.id))
        )
      }

      return games
    }

    return games.filter(
      game => game.player1User?.id === playerUserId || game.player2User?.id === playerUserId
    )
  }, [games, matchupTeamA, matchupTeamB, playerUserId, selectedTeamName, users])

  const pageTitle = playerUserId
    ? `${(selectedUserDisplayName ?? 'Player').toUpperCase()} GAME RESULTS`
    : selectedTeamName
      ? `${getLeagueTeamDisplayName(selectedTeamName).toUpperCase()} GAME RESULTS`
      : matchupTeamA && matchupTeamB
        ? `${matchupTeamA} VS ${matchupTeamB} GAME RESULTS`
        : 'All Game Results'

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Unknown'
    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) {
      return 'Unknown'
    }
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (loading) {
    return <div className="games-list loading">Loading games...</div>
  }

  if (error) {
    return (
      <div className="games-list">
        <div className="error-message">{error}</div>
      </div>
    )
  }

  return (
    <div className="games-list">
      <div className="games-header">
        <button
          className="btn btn-secondary back-btn"
          onClick={() => navigate(hasMatchupFilter ? '/team-matchup-matrix' : playerUserId || selectedTeamName ? '/standings' : '/')}
        >
          ← Back
        </button>
        <h1>{pageTitle}</h1>
      </div>

      {displayedGames.length === 0 ? (
        <div className="empty-state">
          <p>
            {playerUserId
              ? 'No games found for this player yet.'
              : selectedTeamName
                ? 'No games found for this team yet.'
                : hasMatchupFilter
                  ? 'No games found for this matchup yet.'
                  : 'No games have been uploaded yet.'}
          </p>
        </div>
      ) : (
        <div className="games-container">
          {displayedGames.map((game) => (
            <div
              key={game.id}
              className="game-card clickable"
              onClick={() => navigate(`/games/${game.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(`/games/${game.id}`)
                }
              }}
            >
              {(game.player1User || game.player2User) ? (
                <div className="teams">
                  {game.player1User && (
                    <div className="team">
                      <div className="team-info">
                        <div className="coach-row">
                          <UserAvatar
                            displayName={game.player1User.displayName}
                            discordId={game.player1User.discordId}
                            discordAvatar={game.player1User.discordAvatar}
                            size="sm"
                            className="coach-avatar"
                          />
                          <span className="coach-name">{game.player1User.displayName}</span>
                        </div>
                        <div className="team-bottom-row">
                          <TeamHelmet
                            abbreviation={game.player1User.teamAbbreviation}
                            size="sm"
                            className="team-helmet"
                          />
                          <span className="team-abbreviation">
                            {game.player1User.teamAbbreviation}
                          </span>
                          <span className="team-score">{game.homeScore}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="vs">vs</div>
                  {game.player2User && (
                    <div className="team">
                      <div className="team-info">
                        <div className="coach-row">
                          <UserAvatar
                            displayName={game.player2User.displayName}
                            discordId={game.player2User.discordId}
                            discordAvatar={game.player2User.discordAvatar}
                            size="sm"
                            className="coach-avatar"
                          />
                          <span className="coach-name">{game.player2User.displayName}</span>
                        </div>
                        <div className="team-bottom-row">
                          <TeamHelmet
                            abbreviation={game.player2User.teamAbbreviation}
                            size="sm"
                            className="team-helmet"
                          />
                          <span className="team-abbreviation">
                            {game.player2User.teamAbbreviation}
                          </span>
                          <span className="team-score">{game.awayScore}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="teams">
                  <div className="team">
                    <div className="team-info">
                      <div className="team-only-row">
                        <span className="team-only-name">{game.homeTeam}</span>
                      </div>
                      <div className="team-bottom-row">
                        <TeamHelmet
                          abbreviation={game.homeTeam}
                          size="sm"
                          className="team-helmet"
                        />
                        <span className="team-abbreviation">{game.homeTeam}</span>
                        <span className="team-score">{game.homeScore}</span>
                      </div>
                    </div>
                  </div>
                  <div className="vs">vs</div>
                  <div className="team">
                    <div className="team-info">
                      <div className="team-only-row">
                        <span className="team-only-name">{game.awayTeam}</span>
                      </div>
                      <div className="team-bottom-row">
                        <TeamHelmet
                          abbreviation={game.awayTeam}
                          size="sm"
                          className="team-helmet"
                        />
                        <span className="team-abbreviation">{game.awayTeam}</span>
                        <span className="team-score">{game.awayScore}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className="game-meta">
                <span>
                  {formatDate(game.playedAt ?? game.uploadedAt)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

